import FemUtils from '@fem/utils/FemUtils';
import axios from 'axios';
import jwtDecode from 'jwt-decode';
import {
  postLogin,
  postNewAssociationMemberships,
  postNewClient,
  postNewUser,
} from '@fem/cruds/userCrud';
import { BASE_URL } from '@fem/cruds/constants';

/* eslint-disable camelcase */

class JwtService extends FemUtils.EventEmitter {
  init() {
    this.setInterceptors();
    this.handleAuthentication();
  }

  setInterceptors = () => {
    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (err) => {
        return new Promise((resolve, reject) => {
          if (
            err.response.status === 401 &&
            err.config &&
            !err.config.__isRetryRequest
          ) {
            // if you ever get an unauthorized response, logout the user
            this.emit('onAutoLogout', 'Invalid access_token');
            this.setSession(null);
          }
          throw err;
        });
      },
    );
  };

  handleAuthentication = () => {
    const access_token = this.getAccessToken();
    if (!access_token) {
      this.emit('onNoAccessToken');

      return;
    }

    if (this.isAuthTokenValid(access_token)) {
      this.setSession(access_token);
      this.emit('onAutoLogin', true);
    } else {
      this.setSession(null);
      this.emit('onAutoLogout', 'access_token expired');
    }
  };

  createUser = (data, extraData) => {
    return new Promise((resolve, reject) => {
      postNewUser(data)
        .then((response) => {
          const userData = response;
          if (userData.data.user) {
            this.setSession(userData.data.jwt);
            resolve(userData.data.user);
          } else {
            reject(userData.data.error);
          }
        })
        .catch((e) => reject(e));
    });
  };

  createClient = async (data, extraData) => {
    return new Promise((resolve, reject) => {
      postNewClient(data)
        .then((response) => {
          const clientData = response.data.data;
          if (clientData) {
            if (extraData.showConvention === 'hide') {
              resolve(clientData);
            } else if (extraData.showConvention === 'show') {
              const membershipsData = {
                data: {
                  name: data.data.company_name,
                  card: extraData.cardNumber,
                  association: extraData.associationId,
                  unofficial: true,
                  client: clientData.id,
                },
              };
              postNewAssociationMemberships(membershipsData)
                .then((response) => {
                  const associationData = response.data.data;
                  if (associationData) {
                    resolve(clientData);
                  } else {
                    reject(response.data.error);
                  }
                })
                .catch((e) => reject(e));
            }
          } else {
            reject(response.data.error);
          }
        })
        .catch((e) => reject(e));
    });
  };

  signInWithEmailAndPassword = (email, password) => {
    return new Promise((resolve, reject) => {
      postLogin({
        identifier: email,
        password: password,
      })
        .then((response) => {
          if (response.data.user) {
            this.setSession(response.data.jwt);
            resolve(response.data.user);
          } else {
            reject(response.data.error);
          }
        })
        .catch((e) => reject(e));
    });
  };

  signInWithToken = () => {
    return new Promise((resolve, reject) => {
      axios
        .get(`${BASE_URL}/api/users/me`, {
          data: {
            access_token: this.getAccessToken(),
          },
        })
        .then((response) => {
          if (response.data.id) {
            resolve(response.data);
          } else {
            this.logout();
            reject(new Error('Failed to login with token.'));
          }
        })
        .catch((error) => {
          this.logout();
          reject(new Error('Failed to login with token.'));
        });
    });
  };

  updateUserData = (user) => {
    return axios.post('/api/auth/user/update', {
      user,
    });
  };

  setSession = (access_token) => {
    if (access_token) {
      localStorage.setItem('jwt_access_token', access_token);
      axios.defaults.headers.common.Authorization = `Bearer ${access_token}`;
    } else {
      localStorage.removeItem('jwt_access_token');
      delete axios.defaults.headers.common.Authorization;
    }
  };

  logout = () => {
    this.setSession(null);
  };

  isAuthTokenValid = (access_token) => {
    if (!access_token) {
      return false;
    }
    const decoded = jwtDecode(access_token);
    const currentTime = Date.now() / 1000;
    if (decoded.exp < currentTime) {
      console.warn('access token expired');
      return false;
    }

    return true;
  };

  getAccessToken = () => {
    return window.localStorage.getItem('jwt_access_token');
  };
}

const instance = new JwtService();

export default instance;
